/**
 * usePageTracking Hook
 * 
 * PURPOSE: Track page views on every route change with explicit scope classification.
 * 
 * SCOPE CLASSIFICATION:
 * - "public": Landing, about, pricing, FAQ, contact, public library
 * - "reader": Authenticated reader pages (home, library, player, account)
 * - "admin": All /admin/* routes
 * 
 * TRACKING BEHAVIOR:
 * - Emits page_view on every route change
 * - Emits visit_start on first route (app load)
 * - Emits visit_end on page unload (with duration)
 * 
 * STORAGE:
 * - anonymous_id: Persistent in localStorage (survives sessions)
 * - session_id: Per-visit in sessionStorage
 * - visit_start: Timestamp for duration calculation
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  initEngagementTracking,
  trackPageView,
  getSessionId,
  getAnonymousId,
  logEngagementEvent
} from '../services/engagementTracking';

// Route classification rules
const ROUTE_CLASSIFICATIONS = {
  // Admin pages
  admin: ['/admin'],
  
  // Public pages (pre-auth)
  public: [
    '/',
    '/login',
    '/about',
    '/pricing',
    '/faq',
    '/contact',
    '/public/library'
  ],
  
  // Reader/authenticated pages (default for anything not matching above)
  reader: []  // Catchall for authenticated routes
};

/**
 * Classify a route into page_scope
 * @param {string} pathname - The current route pathname
 * @returns {string} - "public" | "reader" | "admin"
 */
export const classifyRoute = (pathname) => {
  // Admin routes (any route starting with /admin)
  if (pathname.startsWith('/admin')) {
    return 'admin';
  }
  
  // Public routes (exact matches or prefix matches)
  for (const publicRoute of ROUTE_CLASSIFICATIONS.public) {
    if (pathname === publicRoute) {
      return 'public';
    }
  }
  
  // Everything else is reader (authenticated) scope
  return 'reader';
};

/**
 * Extract page name from pathname for readable labels
 * @param {string} pathname 
 * @returns {string}
 */
const getPageName = (pathname) => {
  if (pathname === '/') return 'Landing';
  
  // Remove leading slash and convert to readable format
  const parts = pathname.slice(1).split('/');
  return parts
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' > ');
};

/**
 * Hook for tracking page views on route changes
 * 
 * Usage: Call once in App.js inside the BrowserRouter
 * 
 * @example
 * function App() {
 *   return (
 *     <BrowserRouter>
 *       <PageTracker />
 *       <Routes>...</Routes>
 *     </BrowserRouter>
 *   );
 * }
 */
export const usePageTracking = () => {
  const location = useLocation();
  const isInitialized = useRef(false);
  const previousPath = useRef(null);
  const visitStartTime = useRef(null);

  // Initialize tracking on first mount
  useEffect(() => {
    if (!isInitialized.current) {
      isInitialized.current = true;
      visitStartTime.current = Date.now();
      
      // Log visit start
      const pageScope = classifyRoute(location.pathname);
      logEngagementEvent('visit_start', 'web_visit', {
        entry_page: location.pathname,
        page_scope: pageScope,
        page_name: getPageName(location.pathname),
        referrer: document.referrer || 'direct',
        timestamp: new Date().toISOString()
      });
      
      // Log initial page view
      logEngagementEvent('page_view', 'web_visit', {
        route: location.pathname,
        page_scope: pageScope,
        page_name: getPageName(location.pathname),
        page_title: document.title,
        is_entry_page: true,
        timestamp: new Date().toISOString()
      });
      
      previousPath.current = location.pathname;
      
      // Setup visit_end handlers
      const handleVisitEnd = () => {
        const durationSeconds = Math.round((Date.now() - visitStartTime.current) / 1000);
        const exitPageScope = classifyRoute(window.location.pathname);
        
        logEngagementEvent('visit_end', 'web_visit', {
          exit_page: window.location.pathname,
          page_scope: exitPageScope,
          duration_seconds: durationSeconds,
          pages_viewed: window.sessionStorage.getItem('na_pages_viewed') || '1',
          timestamp: new Date().toISOString()
        });
      };
      
      window.addEventListener('beforeunload', handleVisitEnd);
      
      // Also track on visibility change (mobile browsers may not fire beforeunload)
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'hidden') {
          handleVisitEnd();
        }
      };
      document.addEventListener('visibilitychange', handleVisibilityChange);
      
      // Cleanup
      return () => {
        window.removeEventListener('beforeunload', handleVisitEnd);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  }, []);

  // Track route changes
  useEffect(() => {
    // Skip if same path (React strict mode double-render)
    if (previousPath.current === location.pathname) {
      return;
    }
    
    // Skip initial render (already handled above)
    if (previousPath.current === null) {
      previousPath.current = location.pathname;
      return;
    }
    
    const pageScope = classifyRoute(location.pathname);
    const previousScope = classifyRoute(previousPath.current);
    
    // Log page view with scope
    logEngagementEvent('page_view', 'web_visit', {
      route: location.pathname,
      page_scope: pageScope,
      page_name: getPageName(location.pathname),
      page_title: document.title,
      previous_route: previousPath.current,
      previous_scope: previousScope,
      is_entry_page: false,
      timestamp: new Date().toISOString()
    });
    
    // Increment pages viewed counter
    const pagesViewed = parseInt(window.sessionStorage.getItem('na_pages_viewed') || '1') + 1;
    window.sessionStorage.setItem('na_pages_viewed', pagesViewed.toString());
    
    previousPath.current = location.pathname;
  }, [location.pathname]);

  return null;
};

/**
 * PageTracker Component
 * 
 * Wrapper component for usePageTracking hook.
 * Place this inside BrowserRouter but before Routes.
 */
export const PageTracker = () => {
  usePageTracking();
  return null;
};

export default usePageTracking;
